import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { TextAreaWithCharLimit } from "@ui/components/common/text-area-with-char-limit";
import { ToastContext } from "@ui/components/common/toast-provider";
import { row, stack } from "@ui/style";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext } from "react";
import { Controller, useForm } from "react-hook-form";

interface FormData {
	name: string;
}

export interface DuplicateAsTemplateDialogProps extends DialogProps {
	programName: string;
	onSubmit: (name: string) => void;
}

export const DuplicateAsTemplateDialog: React.FC<DuplicateAsTemplateDialogProps> = ({
	programName,
	onSubmit,
	...otherProps
}) => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const { control, handleSubmit } = useForm<FormData>({
		defaultValues: { name: programName },
	});

	const [saveTemplate, isLoading] = useAsyncOperation(
		useCallback(
			async (name: string) => {
				try {
					onSubmit(name);
				} catch (e) {
					showToast(format("global.errorToast.title"), format("global.duplicateProgramOperation.errorToast.message"));
				}
			},
			[format, onSubmit, showToast],
		),
		{},
	);

	return (
		<BaseDialog
			header={format("patientDetails.patientPrograms.duplicateAsTemplateDialog.title")}
			footer={
				<ButtonContainer>
					<Button
						label={format("patientDetails.patientPrograms.duplicateAsTemplateDialog.duplicateButton")}
						loading={isLoading}
						type="submit"
						onClick={handleSubmit(({ name }) => saveTemplate(name))}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<DialogContainer>
				<Form onSubmit={handleSubmit(({ name }) => saveTemplate(name))}>
					<FormField>
						<div className="p-size-m">
							{format("patientDetails.patientPrograms.duplicateAsTemplateDialog.description")}
						</div>
						<label className="p-size-m">
							{format("patientDetails.patientPrograms.duplicateAsTemplateDialog.label")}
						</label>
						<Controller
							name="name"
							control={control}
							rules={{
								required: format("global.form.field.requiredError"),
								maxLength: {
									value: APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH,
									message: format("global.templateName.maxLength"),
								},
							}}
							render={({ field: { onChange, value, ...otherFields }, fieldState }) => (
								<TextAreaWithCharLimit
									{...otherFields}
									autoComplete="off"
									autoFocus={!value}
									text={value}
									onTextChange={onChange}
									errorMessage={fieldState.error?.message}
									charLimit={APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH}
									placeholder={format("templateBuilder.templateNameDialog.nameInput.placeholder")}
								/>
							)}
						/>
					</FormField>
				</Form>
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")}
	height: 100%;
`;

const ButtonContainer = styled.div`
	${row("S", "flexend")}
`;

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")}
	height: 100%;
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
